import { getAxisGroupNode } from './axisNodes'

function isTickTextNode(node) {
  if (!(node instanceof Element)) return false
  if (node.closest('.tick')) return true
  const parentClass = (node.parentElement?.getAttribute('class') || '').toLowerCase()
  return parentClass.includes('tick')
}

function getAxisTitleNodes(axisGroup) {
  if (!axisGroup) return []

  const candidates = Array.from(axisGroup.querySelectorAll('text')).filter(
    (node) => !isTickTextNode(node) && Boolean(node.textContent?.trim())
  )

  return candidates.filter((node) => {
    const className = (node.getAttribute('class') || '').toLowerCase()
    const parentClass = (node.parentElement?.getAttribute('class') || '').toLowerCase()
    const parentId = (node.parentElement?.getAttribute('id') || '').toLowerCase()
    const directTextChildren = Array.from(axisGroup.children || []).filter(
      (child) => child.tagName?.toLowerCase() === 'text'
    )
    const isLastDirectTextChild =
      directTextChildren[directTextChildren.length - 1] === node
    return (
      className.includes('label') ||
      className.includes('title') ||
      parentClass.includes('label') ||
      parentClass.includes('title') ||
      parentId.includes('label') ||
      parentId.includes('title') ||
      isLastDirectTextChild ||
      candidates.length === 1
    )
  })
}

function normalizeNumber(value) {
  const nextValue = Number(value)
  return Number.isFinite(nextValue) ? nextValue : 0
}

function isAxisTitleCandidate(node) {
  if (!(node instanceof Element)) return false
  if (node.closest('.tick')) return false

  const className = (node.getAttribute('class') || '').toLowerCase()
  const parentClass = (node.parentElement?.getAttribute('class') || '').toLowerCase()
  const parentId = (node.parentElement?.getAttribute('id') || '').toLowerCase()
  return (
    className.includes('label') ||
    className.includes('title') ||
    parentClass.includes('label') ||
    parentClass.includes('title') ||
    parentId.includes('label') ||
    parentId.includes('title')
  )
}

function getAxisBounds(axisGroup, excludedNodes = []) {
  try {
    const excluded = new Set(excludedNodes.filter(Boolean))
    const childBoxes = Array.from(axisGroup.children || [])
      .filter(
        (node) =>
          node &&
          node.nodeType === 1 &&
          !excluded.has(node) &&
          !isAxisTitleCandidate(node)
      )
      .map((node) => {
        try {
          return node.getBBox()
        } catch (error) {
          return null
        }
      })
      .filter(Boolean)

    if (!childBoxes.length) {
      const box = axisGroup.getBBox()
      return {
        left: box.x,
        right: box.x + box.width,
        top: box.y,
        bottom: box.y + box.height,
        width: box.width,
        height: box.height,
      }
    }

    const left = Math.min(...childBoxes.map((box) => box.x))
    const right = Math.max(...childBoxes.map((box) => box.x + box.width))
    const top = Math.min(...childBoxes.map((box) => box.y))
    const bottom = Math.max(...childBoxes.map((box) => box.y + box.height))

    return {
      left,
      right,
      top,
      bottom,
      width: right - left,
      height: bottom - top,
    }
  } catch (error) {
    return null
  }
}

function setTextPosition(textNode, attrs = {}) {
  Object.entries(attrs).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      textNode.setAttribute(key, String(value))
    }
  })
}

function positionXAxisTitle(textNode, axisBounds) {
  const x = axisBounds.left + axisBounds.width / 2
  // axisBounds is in the axis group's LOCAL coordinate space, so all
  // positioning must use only local values — mixing svgMetrics (root coords)
  // with axisBounds (local coords) breaks the Math.min clamp.
  // Place the title just below the label area with a modest fixed gap.
  const y = axisBounds.bottom + 14

  setTextPosition(textNode, {
    x,
    y,
    'text-anchor': 'middle',
    'dominant-baseline': 'hanging',
  })

  textNode.style.textAnchor = 'middle'
  textNode.style.dominantBaseline = 'hanging'
}

function positionYAxisTitle(textNode, axisBounds, svgMetrics) {
  // axisBounds is in the axis group's LOCAL coordinate space.
  // Place the title 16px to the left of the leftmost tick-label edge so it
  // never overlaps labels regardless of how wide the left margin is.
  const x = axisBounds.left - 16
  const y = axisBounds.top + axisBounds.height / 2

  setTextPosition(textNode, {
    x,
    y,
    transform: `rotate(-90 ${x} ${y})`,
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
  })

  textNode.style.textAnchor = 'middle'
  textNode.style.dominantBaseline = 'middle'
}

function removeAxisTitleNodes(titleNodes) {
  titleNodes.forEach((node) => {
    node.remove()
  })
}

function markAxisTitleNodes(titleNodes, axis) {
  titleNodes.forEach((node) => {
    node.setAttribute('data-lugsail-axis-title', axis)
  })
}

export function applyAxisTitleLayoutToSvg(
  svgNode,
  svgMetrics = null,
  chartId = null,
  options = {}
) {
  if (!svgNode) return
  const showAxisTitles = options.showAxisTitles !== false
  const textColor = options.textColor || null

  const metrics = svgMetrics || (() => {
    const viewBox = svgNode.getAttribute('viewBox')
    if (viewBox) {
      const [, , width, height] = viewBox.split(/\s+/).map(Number.parseFloat)
      return {
        x: 0,
        y: 0,
        width: Number.isFinite(width) ? width : 0,
        height: Number.isFinite(height) ? height : 0,
      }
    }
    return {
      x: 0,
      y: 0,
      width: normalizeNumber(svgNode.getAttribute('width')),
      height: normalizeNumber(svgNode.getAttribute('height')),
    }
  })()

  const xAxisGroup = getAxisGroupNode(svgNode, 'x', chartId)
  if (xAxisGroup) {
    const xAxisTitles = getAxisTitleNodes(xAxisGroup)
    if (!showAxisTitles) {
      removeAxisTitleNodes(xAxisTitles)
    } else {
      markAxisTitleNodes(xAxisTitles, 'x')
      const xAxisBounds = getAxisBounds(xAxisGroup, xAxisTitles)
      if (xAxisBounds) {
        xAxisTitles.forEach((node) => {
          positionXAxisTitle(node, xAxisBounds, metrics)
          if (textColor) {
            node.setAttribute('fill', textColor)
            node.style.fill = textColor
          }
        })
      }
    }
  }

  const yAxisGroup = getAxisGroupNode(svgNode, 'y', chartId)
  if (yAxisGroup) {
    const yAxisTitles = getAxisTitleNodes(yAxisGroup)
    if (!showAxisTitles) {
      removeAxisTitleNodes(yAxisTitles)
    } else {
      markAxisTitleNodes(yAxisTitles, 'y')
      const yAxisBounds = getAxisBounds(yAxisGroup, yAxisTitles)
      if (yAxisBounds) {
        yAxisTitles.forEach((node) => {
          positionYAxisTitle(node, yAxisBounds, metrics)
          if (textColor) {
            node.setAttribute('fill', textColor)
            node.style.fill = textColor
          }
        })
      }
    }
  }
}
